<template>
  <!-- 侧边栏布局（said） -->
  <SidebarLayout v-if="useSidebarLayout" />
  <!-- Tabs 布局（top） -->
  <div v-else class="main-page" :class="{ tabbar: tabbarVisible }">
    <!--
      状态栏“视觉背景填充层：
      - 原生 StatusBar 保持透明（backgroundColor: '#00000000'）
      - overlaysWebView=true 时，页面内容会延伸到状态栏下方
      - 通过这层用 safe-area 顶部高度铺一块背景色，让“状态栏区域”与页面顶部背景统一
      背景色来源：main-page 上绑定的 CSS 变量 --statusbar-bg
    -->
    <div class="statusbar-fill" />
    <!-- 使用单一 RouterView，避免组件重复渲染 -->
    <RouterView v-slot="{ Component, route }">
      <component
        :is="Component"
      />
    </RouterView>
  </div>
  <!-- 底部导航栏显示逻辑，支持动态路由 -->
  <var-bottom-navigation 
    v-model:active="activeTab" 
    v-if="tabbarVisible" 
    @change="tabSwitch"
    :style="{ height: menubottomHeight }"
    :key="`nav-${activeTab}`">
    <var-bottom-navigation-item v-for="(item, index) in tabItem" :key="index" :name="String(index)" >
      <template #icon="{ active }">
        <CustomIcon 
          v-if="hasIcon(item.icon)"
          :config="iconConfig(item.icon, active, 24, 'var(--color-primary)', 'var(--color-text)')"
          :active="active"
        />

        <!-- 当没有图标时显示默认文字图标  -->
        <!-- <span v-else class="fallback-icon" :style="{ color: active ? 'var(--color-primary)' : 'var(--color-text)' }">
          {{ getDefaultIconText(item) }}
        </span> -->
      </template>
      <span class="titem" :style="{ color: String(activeTab) === String(index) ? 'var(--color-primary)' : 'var(--color-text)' }">
        {{ item.title || item.key }}
      </span>
    </var-bottom-navigation-item>
  </var-bottom-navigation>
</template>

<script lang="ts" setup name="BasicLayoutPage">
import { useUserStore } from 'd3components';
import { useRouter, useRoute } from 'vue-router';
import { ref, computed, watch, provide } from 'vue';
import CustomIcon from 'd3components/src/components/item-d3/d3/d3_customIcon.vue';
import SidebarLayout from '../components/dynamic/SidebarLayout.vue';
import { getKeyStr ,removeKey, setKey, setKeyJsonStr ,getKeyJson,updateKeyJson,getsubKey, removeKeys} from 'd3components/src/utils/localStorage';


const router = useRouter();
const route = useRoute();
const userStore = useUserStore();

// 从配置接口获取布局
const layoutType = computed(() => {
  const config = (userStore as any).erpConfig || {};
  return config.sa_theme_layout || 'top'; // 默认使用 top 布局
  // return 'said';
});

const menubottomHeight = computed(() => {
  const config = (userStore as any).erpConfig || {};
  return config.sa_menu_tabs_height || '60px';
});

// 是否使用侧边栏布局
const useSidebarLayout = computed(() => layoutType.value === 'said');

// 根据布局类型控制是否隐藏 PageTabs
provide('hidePageTabsHeader', useSidebarLayout);

// Tab 固定项（仅用于底部导航）
const alwaysShowItems = [{ 
  key: 'member', 
  title: '我的', 
  path: '/member', 
  icon: 'user',
  index: -1,
  name: undefined,
  redirect: undefined,
  children: []
}];

// =====获取菜单节点的唯一标识符 =====
const topKey = (n: any) => n.name ?? n.path ?? '';

// 图标配置：默认使用全局主题色
const iconConfig = (
  url: any,
  active: boolean,
  size = 20,
  activeColor = 'var(--color-primary)',
  inactiveColor = 'var(--color-text)'
) => ({
  attr: {
    cic_tp: 1,
    cic_sz: size,
    cic_url: typeof url === 'string' ? url : (url?.attr?.cic_url || url?.url || ''),
    cic_c: active ? activeColor : inactiveColor,
  },
});

const hasIcon = (icon: any) => {
  if (typeof icon === 'string') return icon.trim() !== '';
  if (icon?.attr?.cic_url || icon?.url) return true;
  return false;
};
// // 获取默认图标文字的函数   先不启用---不做删除
// const getDefaultIconText = (item: any) => {
  
//   // 优先使用标题的第一个字符
//   if (item.title && item.title.trim() !== '') {
//     return item.title.charAt(0);
//   }
//   // 其次使用 t 字段的第一个字符
//   if (item.t && String(item.t).trim() !== '') {
//     return String(item.t).charAt(0);
//   }
//   // 其次使用 m 字段的第一个字符
//   if (item.m && item.m.trim() !== '') {
//     return item.m.charAt(0);
//   }
//   if (item.mn && String(item.mn).trim() !== '') {
//     return String(item.mn).charAt(0);
//   }
//   // 最后使用 key 的第一个字符
//   if (item.key && item.key.trim() !== '') {
//     return item.key.charAt(0).toUpperCase();
//   }
//   // 都没有的话使用默认符号
//   return '•';
// };

// ===== 菜单标准化 =====
type MenuNode = {
  name?: string;
  path?: string;
  redirect?: any;
  title: string;
  children: MenuNode[];
  icon?: any;
};

function normalizeMenu(menu: any): MenuNode {
  const meta = menu?.meta ?? {};
  return {
    name: menu?.name,
    path: menu?.path,
    redirect: menu?.redirect,
    title: meta.title ?? menu?.title ?? menu?.name ?? menu?.path ?? '未命名',
    icon: meta.icon ?? menu?.icon,
    children: (menu?.children ?? []).map(normalizeMenu),
  };
}

// 菜单为Tab项
const normalizeTab = (menu: any, index: number) => {
  const normalized = normalizeMenu(menu);
  return {
    ...normalized,
    index,
    key: menu?.key,
    path: normalized.path?.startsWith('/') ? normalized.path : `/${normalized.path || ''}`,
  };
};

// ===== 顶级菜单 =====
const topMenus = computed<MenuNode[]>(() =>
  Array.isArray((userStore as any).userMenuRouter) ? (userStore as any).userMenuRouter.map(normalizeMenu): []
);

// 底部Tab项
const tabItem = computed(() => {
  const topMenus = (userStore as any).userMenuRouter ?? [];
  if (!Array.isArray(topMenus) || topMenus.length === 0) {
    return [...alwaysShowItems];
  }

  let tabs = topMenus.map(normalizeTab).slice(0, 4); // 最多4个用户tab + 1个"我的" = 5个

  // 确保有"我的"入口
  // const hasMember = tabs.some(item => item.key === 'member' || item.path === '/member');
  // if (!hasMember) tabs.push(...alwaysShowItems);

  return tabs;
});

// ===== 动态路由识别（数据来源：route.matched，功能：判断当前路由是否为动态路由，获取顶级菜单名称） =====
const isDynamicRoute = computed(() => 
  route.matched.some((rec: any) => rec.meta?._isDynamic)
);

// 获取当前路由所属的顶级菜单名称（用于底部导航栏激活状态）
const dynamicTopName = computed<string | undefined>(() => {
  for (const rec of route.matched) {
    if (rec.meta?._isDynamicTop && rec.name) return String(rec.name);
    if (rec.meta?._topName) return String(rec.meta._topName);
  }
});


// ===== 激活判断 =====
const isActive = (node: MenuNode) => {
  if (node.name) {
    if (route.matched.some(r => r.name === node.name)) return true;
    if (route.name === node.name) return true;
  }
  if (node.path) {
    const p = node.path.startsWith('/') ? node.path : `/${node.path}`;
    return route.path === p || route.path.startsWith(`${p}/`);
  }
  return false;
};
// 判断顶级菜单是否激活
const isTopActive = (top: MenuNode) =>
  (dynamicTopName.value && top.name === dynamicTopName.value) ||
  isActive(top);
// 当前激活的顶级菜单
const activeTopMenu = computed(() => topMenus.value.find(isTopActive));
// ===== 导航跳转 =====
const navigate = (node: MenuNode | any) => {
  if (!node) return null;
  let target: string | null = null;
  // 优先 redirect
  if (node.redirect) {
    target = router.resolve(node.redirect).path;
  } 
  // 尝试跳转第一个子菜单
  else if (node.children?.[0]) {
    const firstChild = node.children[0];
    if (firstChild.redirect) {
      target = router.resolve(firstChild.redirect).path;
    } else if (firstChild.name) {
      target = router.resolve({ name: firstChild.name }).path;
    }
  }
  // 使用 name 跳转
  else if (node.name) {
    target = router.resolve({ name: node.name }).path;
  }
  // 使用 path 跳转
  else if (node.path) {
    target = node.path.startsWith('/') ? node.path : `/${node.path}`;
  }

  if (!target || target === route.path) return;
  router.push(target);
};

// ===== 底部Tab相关 =====
const activeTab = ref<string | number>(0);
// 底部导航栏显示逻辑：仅在 top 布局下，且满足显示条件
const tabbarVisible = computed(() => {
  // if (useSidebarLayout.value) return false; // 侧边栏布局不显示底部导航
  return route.path.startsWith('/member') || isDynamicRoute.value;
});

// 统一路由变化处理：Tab 激活
watch(
  () => route.fullPath,
  () => {
    const top = activeTopMenu.value;

    // Tab：根据 activeTopMenu 计算激活状态
    if (tabbarVisible.value && top) {
      const routeIndex = tabItem.value.findIndex(
        item => item.name === top.name || item.path === top.path
      );
      if (routeIndex !== -1) {
        activeTab.value = String(routeIndex);
      }
    }
  },
  { immediate: true }
);
// 获取用户 Tab 状态
const getUserTabState = (key: string): string | undefined => {
  const userRecord = getsubKey('mobile', 'user_info')?.[0];
  return userRecord?.tabStates?.[key];
};

// 底部Tab切换
const tabSwitch = (index: string | number) => {
  const numIndex = Number(index);
  const item = tabItem.value[numIndex];
  if (!item) return;

  // 立即更新选中状态
  activeTab.value = String(numIndex);

  // 用户菜单：尝试读取保存的路径
  if ((item as any).index !== undefined && (item as any).index >= 0) {
    const key = topKey(item);
    const savedPath = key ? getUserTabState(`lastTabPath:${key}`) : undefined;
    if (savedPath && savedPath !== route.path) {
      router.push(savedPath);
      return;
    }
  }

  // 统一使用 navigate 处理跳转
  navigate(item);
};

// const navTitle = computed(() => {
//   let mlist = tabItem.value;
//   if (mlist && mlist.length > 0) {
//     let curM = mlist[activeTab.value];
//     return curM['title'];
//   }
//   return '移动端';
// });

// const goBack = () => {
//   router.go(-1);
// };
</script>
<style >
/* 强制底部导航栏样式 - 条件性考虑安全区域 */
.var-bottom-navigation {
  position: fixed !important;
  bottom: 0 !important;
  left: 0 !important;
  right: 0 !important;
  z-index: 999 !important;
  width: 100% !important;
  background: var(--color-body) !important;
  /* height: 80px !important;
  min-height: 80px !important; */
  border-top: 1px solid hsla(var(--hsl-text), 0.12);
  box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.08);
  /* 确保底部导航栏在键盘弹起时也保持在屏幕底部 */
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
}

.var-bottom-navigation-item,
.var-bottom-navigation-item--active {
  background: transparent !important;
}
</style>
<style scoped lang="scss">
/* ===== 底部导航栏样式 ===== */
:deep(.var-bottom-navigation-item) {
  padding-bottom: 0 !important;
}

:deep(.var-bottom-navigation-item .titem) {
  font-size: 10px !important;
  font-weight: 500 !important;
  margin-top: 4px !important;
  transition: all 0.3s ease !important;
}

:deep(.var-bottom-navigation-item .iconfont) {
  font-size: 22px !important;
  transition: all 0.3s ease !important;
}

:deep(.var-bottom-navigation-item--active .titem) {
  font-weight: 600 !important;
}

:deep(.var-bottom-navigation-item--active .iconfont) {
  transform: scale(1.1) !important;
}

.main-page {
  box-sizing: border-box;
  width: 100%;
  position: relative;
  /* 保持状态栏填充色与 tabs 头部一致，避免顶部背景色不统一 */
  --statusbar-bg: var(--color-body);
  /* 统一默认底部导航高度，便于后续参与高度计算 */
  --bottom-bar-h: v-bind(menubottomHeight);
  /* 让主体内容在有安全区域时仍能占满除状态栏和底部安全区外的空间 */
  min-height: calc(100vh - var(--safe-area-inset-top) - var(--safe-area-inset-bottom));
  background: var(--color-body);
}

 /* 覆盖状态栏的安全区域高度，用于在原生透明状态栏下“补一层”顶部背景色 */
 .statusbar-fill {
  position: fixed;
  top: 0;
  left: var(--safe-area-inset-left);
  right: var(--safe-area-inset-right);
  height: var(--safe-area-inset-top);
  background: var(--statusbar-bg, var(--color-body));
  z-index: 5;
  pointer-events: none;
 }

/* 状态栏适配完成后的样式 - 只在需要适配的设备上生效 */
:global(.needs-status-bar-adaptation) .main-page.safe-area-adapted {
  /* 确保内容不被状态栏遮挡 */
  min-height: calc(100vh - var(--safe-area-inset-top) - var(--safe-area-inset-bottom));
}


/* 有底部导航时的样式 */
.main-page.tabbar {
  /* 为底部导航栏预留空间，扣除状态栏与底部安全区 */
  height: calc(100vh - var(--safe-area-inset-top) - var(--safe-area-inset-bottom) - var(--bottom-bar-h));
  /* 内容直接铺满到底部导航栏，无额外间隔 */
}

/* 状态栏适配完成且有底部导航时的样式 - 只在需要适配的设备上生效 */
:global(.needs-status-bar-adaptation) .main-page.tabbar {
  /* 复用同一套扣减公式，避免重复扣减 */
  height: calc(100vh - var(--safe-area-inset-top) - var(--safe-area-inset-bottom) - var(--bottom-bar-h));
}

:global(.needs-status-bar-adaptation) .main-page.tabbar.safe-area-adapted {
  /* 同上，保持与上一规则一致，防止重复扣减 */
  height: calc(100vh - var(--safe-area-inset-top) - var(--safe-area-inset-bottom) - var(--bottom-bar-h));
}

.fallback-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  font-size: 12px;
  font-weight: bold;
  border-radius: 50%;
  background-color: hsla(var(--hsl-text), 0.12);
  transition: all 0.3s ease;

  /* 当激活时的样式会通过内联样式的 color 属性控制 */
  &:hover {
    background-color: hsla(var(--hsl-text), 0.18);
  }
}

.iconfont {
  font-size: 36px;
}

.titem {
  display: block;
  font-size: 3vw;
  line-height: 4vw;
}
</style>